import {
	CloseButton,
	Combobox,
	type ComboboxProps,
	Input,
	InputBase,
	ScrollArea,
	Tooltip,
	useCombobox,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { UseFilterReturn } from '../hooks/useFilter';
import { query } from '../util/api/query';
import type { PharmaciesListDto } from '../util/types/PharmaciesListDto';

export interface SelectorGroupItem {
	group: string;
	items: string[];
}

interface PharmacySelectorProps extends ComboboxProps {
	filter: UseFilterReturn;
}

export function BranchSelector({
	filter,
	variant = 'borderless-filled',
	...comboboxProps
}: PharmacySelectorProps) {
	const { organisation } = useParams();

	const pharmaciesQuery = query<PharmaciesListDto>(
		`${organisation}/branches/list-pharmacies`,
	);
	const [pharmacies, setPharmacies] = useState<SelectorGroupItem[]>([]);

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	useEffect(() => {
		if (pharmaciesQuery.data) {
			const value = pharmaciesQuery.data.value;

			setPharmacies([
				{ group: 'Areas', items: value.areas },
				{ group: 'Individual', items: value.pharmacies },
			]);
		}
	}, [pharmaciesQuery.data]);

	if (!filter.value) return null;

	return (
		<Combobox
			store={combobox}
			withinPortal={false}
			onOptionSubmit={(val) => {
				filter.setPharmacy(val);
				combobox.closeDropdown();
			}}
			{...comboboxProps}
		>
			<Combobox.Target>
				<Tooltip label="Filter by branch or area...">
					<InputBase
						component="button"
						type="button"
						pointer
						rightSectionPointerEvents="none"
						onClick={() => combobox.toggleDropdown()}
						variant={variant}
						leftSection={
							filter.value.pharmacyGroupName !== null ? (
								<CloseButton
									size="sm"
									onMouseDown={(event) => event.preventDefault()}
									onClick={() => filter.setPharmacy('all')}
									aria-label="Clear value"
								/>
							) : (
								<Combobox.Chevron />
							)
						}
					>
						{filter.value.pharmacyGroupName || (
							<Input.Placeholder w="200px">Select branch...</Input.Placeholder>
						)}
					</InputBase>
				</Tooltip>
			</Combobox.Target>

			<Combobox.Dropdown w="200px">
				<Combobox.Options>
					<ScrollArea.Autosize type="scroll" mah={300}>
						{pharmacies.map((group) => (
							<Combobox.Group key={group.group} label={group.group}>
								{group.items.map((item) => (
									<Combobox.Option value={item} key={item}>
										{item}
									</Combobox.Option>
								))}
							</Combobox.Group>
						))}
					</ScrollArea.Autosize>
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
}
